export const QUALITY_MAP = ['poor', 'common', 'fine', 'exquisite', 'epic', 'legendary', 'mythic'];

/**
 * Gets a clean abbreviation for an item name to display in the UI tables.
 * Removes filler words and item slot names.
 */
export function getAbbreviation(item) {
    if (!item || !item.name) return 'None';
    const name = translateGearName(item.name);
    const cleanName = name.replace(/\b(Helm|Helmet|Chest|Pants|Boots|Weapon|Ring|Trinket|Super Sword)\b/gi, '').replace(/'s\b/gi, '');
    const words = cleanName.trim().split(/\s+/).filter(w => !['of', 'the', 'and', 'in'].includes(w.toLowerCase()));

    let abbr = 'None';
    if (words.length > 0 && words[0] !== '') {
        if (words.length === 1) abbr = words[0];
        else abbr = `${words[0]} ${words[1].charAt(0).toUpperCase()}.`;
    }

    if (abbr !== 'None') {
        const quality = item.quality || 'legendary';
        const level = item.level || '';

        let qAbbr = quality.charAt(0).toUpperCase();
        if (quality.toLowerCase() === 'epic') {
            qAbbr = 'Ep';
        } else if (quality.toLowerCase() === 'exquisite') {
            qAbbr = 'Ex';
        }

        const qualityClass = `quality-text-${quality.toLowerCase()}`;

        return `${abbr}<br><span class="${qualityClass}" style="font-size: 0.9em;">(${qAbbr}${level})</span>`;
    }
    return abbr;
}

/**
 * Gets the HTML for a class indicator icon.
 */
export function getIndicatorHtml(itemClass) {
    if (!itemClass || typeof itemClass !== 'string') return '';
    const lowerClass = itemClass.toLowerCase();
    if (lowerClass === 'infantry') return '<span class="class-indicator indicator-infantry">I</span>';
    if (lowerClass === 'ranged') return '<span class="class-indicator indicator-ranged">R</span>';
    if (lowerClass === 'cavalry') return '<span class="class-indicator indicator-cavalry">C</span>';
    if (lowerClass === 'dragon') return '<span class="class-indicator indicator-dragon">D</span>';
    return `<span class="class-indicator indicator-default">${itemClass.charAt(0).toUpperCase()}</span>`;
}

// Locale Translation Dictionary Cache
let _gearTranslationDictionary = {};
let _translationsLoaded = false;

/**
 * Initializes the translation dictionary by fetching the correct local JSON file
 * based on the user's navigator.language fallback chain.
 */
export async function initTranslations() {
    if (_translationsLoaded) return;
    
    try {
        const fullLang = navigator.language || 'en'; // e.g. 'es-MX'
        const baseLang = fullLang.split('-')[0];     // e.g. 'es'
        
        let targetPath = `/static/locales/${baseLang}.json`;
        
        // Attempt to fetch the base language
        let res = await fetch(targetPath);
        if (!res.ok) {
            // Failed, fallback to English (no translation needed, dictionary remains empty)
            _translationsLoaded = true;
            return;
        }
        
        _gearTranslationDictionary = await res.json();
    } catch (e) {
        console.warn("Failed to load translation dictionaries, defaulting to English.", e);
    }
    _translationsLoaded = true;
}

/**
 * Translates a gear name if a translation exists in the current dictionary.
 * Otherwise returns the English default.
 */
export function translateGearName(englishName) {
    if (!englishName) return '';
    return _gearTranslationDictionary[englishName] || englishName;
}

/**
 * Formats a number using the browser's native Intl.NumberFormat.
 * Uses the system's default locale automatically.
 * @param {number|string} value - The number to format
 * @param {boolean} isPercent - Whether the value is a percentage (requires 2 decimal places)
 * @param {boolean} isWhole - Whether the value must be a whole number
 * @returns {string} The localized number string
 */
export function formatNumberLocally(value, isPercent = false, isWhole = false) {
    const num = Number(value);
    if (isNaN(num)) return '';

    let minDigits = 1;
    let maxDigits = 1;

    if (isPercent) {
        minDigits = 2;
        maxDigits = 2;
    } else if (isWhole) {
        minDigits = 0;
        maxDigits = 0;
    }

    return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: minDigits,
        maximumFractionDigits: maxDigits
    }).format(num);
}
